export interface RenterConfigInterface {
  stripePublicKey: string;
  plaidEnv: string;
  plaidLinkToken?: string;
  plaidLinkTokenExpiration?: number;
  enablePaymentStatus?: boolean;
}

// temporary override for UTC timestamp inputs
export type RenterRentAmountChangeRequestNumericInputInterface = Omit<
  RenterRentAmountChangeRequestInterface,
  'effectiveDueDate' | 'renterAcceptanceDate'
> & {
  effectiveDueDate?: number;
  renterAcceptanceDate?: number;
};

export interface RenterRentAmountChangeRequestInterface {
  effectiveDueDate?: string;
  newAmount?: number;
  renterAcceptanceDate?: string;
  expired?: boolean;
}

// temporary override for UTC timestamp inputs
export type RenterRentTermsNumericInputInterface = Omit<
  RenterRentTermsInterface,
  'firstDueDate' | 'rentAmountChangeRequest'
> & {
  firstDueDate: number;
  rentAmountChangeRequest?: RenterRentAmountChangeRequestNumericInputInterface;
};

export interface RenterRentTermsInterface {
  amount: number;
  address: string;
  firstDueDate: string;
  monthToMonth: boolean;
  monthlyRent?: string;
  numberOfPayments: number;
  rentAmountChangeRequest?: RenterRentAmountChangeRequestInterface;
  rentTermsKey?: string;
  addressInfo: Record<string, string>;
}

export interface CreditTrackingInterface {
  enrollmentStatus: string;
  arrayUserToken?: string;
  enrollmentTimestamp?: number;
  optInDate?: string;
  lastFetchedTimestamp?: number;
}

export interface PaymentReportingInterface {
  optedIn: boolean | null;
  optInDate: string;
  creditTracking?: CreditTrackingInterface;
}

export enum RenterOptInReportingStatusType {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  OPTED_OUT = 'OPTED_OUT',
  ACCOUNT_CLOSED = 'ACCOUNT_CLOSED',
  ACCOUNT_DELETED = 'ACCOUNT_DELETED',
}

export interface PaymentReportHistoryInterface {
  optInDate?: string;
  paymentReportMonths?: string[];
  paymentReportingStatus?: RenterOptInReportingStatusType;
}

// temporary override for UTC timestamp inputs
export type RenterStatusNumericInputInterface = Omit<
  RenterStatusInterface,
  'rentTerms' | 'nextPayable' | 'moveInCostsDetails' | 'paymentDetails'
> & {
  rentTerms: RenterRentTermsNumericInputInterface;
  nextPayable: RenterNextPayableNumericInputInterface;
  nextScheduleMonth: RenterNextScheduleMonthNumericInputInterface;
  moveInCostsDetails?: RenterMoveInCostsDetailsNumericInputInterface;
  paymentDetails: RenterPaymentDetailsNumericInputInterface;
  paymentReporting: PaymentReportingInterface;
  enableCreditTracking: boolean;
};

export interface RenterStatusInterface {
  bankAccountVerificationNeeded: boolean;
  landlordProfile: LandlordProfileModel;
  renterProfile: RenterProfileModel;
  nextPayable: RenterNextPayableInterface;
  moveInCostsDetails?: RenterMoveInCostsDetailsInterface;
  currentScheduleMonth: any;
  onboardingBrand: string;
  onboardingStatus: string;
  onboardingComplete: boolean;
  paymentDetails: RenterPaymentDetailsInterface;
  paymentHistory: RenterPaymentHistoryInterface[];
  isColoradoCustomer: boolean;
  paymentMethods: RenterPaymentMethodInterface[];
  paymentMethodTypes: RenterPaymentMethodTypeInterface[];
  rentTerms: RenterRentTermsInterface;
  totalAmountForCreditCard: number;
  dismissibleActions: any;
  rentPaymentSubheading: DashboardSubheadingInterface;
  moveInCostsSubheading?: DashboardSubheadingInterface;
  config: RenterConfigInterface;
  enableCreditTracking: boolean;
}

export interface RenterProfileModel {
  firstName?: string;
  lastName?: string;
  name: string;
  email: string;
}

// temporary override for UTC timestamp inputs
export type RenterPaymentSubscriptionNumericInputInterface = Omit<
  RenterPaymentSubscriptionInterface,
  'firstScheduledDate' | 'nextArrivalDate' | 'nextScheduledDate'
> & {
  firstScheduledDate?: number;
  nextArrivalDate: number;
  nextScheduledDate: number;
};

export interface RenterPaymentSubscriptionInterface {
  amount?: number;
  firstScheduledDate?: string;
  nextArrivalDate: string;
  nextScheduledDate: string;
  recurring: boolean;
  paymentMethodId?: string;
}

export interface RenterAccountInfoInterface {
  accountName: string;
  accountType: string;
  accountLast4: string;
  createdThrough: string;
}

export interface RenterPaymentHistoryInterface {
  propertyId: string;
  transactions: any;
  lateRentPayments: any;
}

// temporary override for UTC timestamp inputs
export type RenterPaymentDetailsNumericInputInterface = Omit<
  RenterPaymentDetailsInterface,
  'paymentSubscriptionDetails'
> & {
  paymentSubscriptionDetails: RenterPaymentSubscriptionNumericInputInterface;
};

export interface RenterPaymentDetailsInterface {
  accountLast4: string;
  accountName: string;
  accountType: string;
  createdThrough: string;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  accountInfo: RenterAccountInfoInterface;
  paymentMethodStatus: string;
}
export interface RentAmountDetailsInterface {
  fee: number;
  rentAmount: number;
  totalAmount: number;
}

export interface OneTimePaymentAmountDetailsInterface {
  groupKey: string;
  baseAmount: number;
  fee: number;
  totalAmount: number;
}

export interface RenterPaymentMethodInterface {
  id: string;
  nickname: string;
  displayName: string;
  accountLast4: string;
  accountType: string;
  paymentMethodStatus?: string;
  isBankAccountVerificationNeeded?: boolean;
  rentAmountDetails: RentAmountDetailsInterface;
  oneTimePaymentAmountDetails: OneTimePaymentAmountDetailsInterface[];
  createdAt: string;
  bankName?: string;
}

export interface RenterPaymentMethodTypeInterface {
  name: string;
  type: string;
  rentFee: string;
  otherFee: string;
}

// temporary override for UTC timestamp inputs
export type RenterNextPayableNumericInputInterface = Omit<RenterNextPayableInterface, 'dueDate'> & {
  dueDate: number;
};

export interface RenterNextPayableInterface {
  scheduleMonth: string;
  dueDate: string;
  numberOfPaymentsRemaining: number;
  amount: number;
  fee: number;
  totalAmount: number;
  isScheduled: boolean;
}

// temporary override for UTC timestamp inputs
export type RenterNextScheduleMonthNumericInputInterface = Omit<RenterNextScheduleMonthInterface, 'dueDate'> & {
  dueDate: number;
};
export interface RenterNextScheduleMonthInterface {
  scheduleMonth: string;
  dueDate: string;
  amount: number;
  totalAmount: number;
  numberOfPaymentsRemaining: number;
}
export interface LandlordProfileModel {
  firstName?: string;
  name: string;
  email: string;
}
export interface DashboardSubheadingInterface {
  title: string;
  statement: StatementEntryInterface[];
}

export interface StatementEntryInterface {
  text: string;
  weight: string;
  color: string;
}

// temporary override for UTC timestamp inputs
export type MoveInCostsNumericInput = Omit<MoveInCosts, 'dateReceived' | 'dateRequested' | 'dueDate'> & {
  dateReceived?: number;
  dateRequested: number;
  dueDate: number;
};

export interface MoveInCosts {
  amount: number;
  dateReceived: string;
  dateRequested: string;
  dueDate: string;
  groupKey: string;
  moveInCostsItems: MoveInCostsItems[];
  paymentCategory: string;
  renterStatus: string;
  totalAmount: number;
  zgFee: number;
  markAsPaidEligible: boolean;
}

export interface MoveInCostsItems {
  amount: number;
  notes?: string;
  type: string;
  subType?: string;
}

// temporary override for UTC timestamp inputs
export type RenterMoveInCostsDetailsNumericInputInterface = Omit<
  RenterMoveInCostsDetailsInterface,
  'dueDate' | 'sendDate'
> & {
  dueDate: number;
  sendDate: number;
};

export interface RenterMoveInCostsDetailsInterface {
  baseAmount: number;
  dueDate: string;
  isScheduled: boolean;
  items: MoveInCostsItems[];
  totalAmount: number;
  zgFee: number;
  status: string;
  sendDate: string;
  groupKey: string;
  paymentMethodId?: string;
}

export interface RenterTransactionProps {
  propertyId: string;
  amount: number;
  accountLast4?: string;
  accountName?: string;
  zgFee: number;
  totalAmount: number;
  year: number;
  month: number;
  day: number;
  renterStatus: string;
  tenant: {
    name: string;
    email: string;
  };
  dueDate: string;
  paymentCategory: string;
  scheduleMonth: string;
  dateCreated: string;
  paymentMethod: string;
  dateRequested: string;
  paymentType: string;
  groupKey: string;
  requestKey: string;
  markAsPaidEligible: boolean;
  paymentPlanInstallmentTitle?: string;
}

export type LateRentModel = {
  rentPaymentSubheading: DashboardSubheadingInterface;
  isPastDue: boolean;
  isPending: boolean;
  rentAmount: number;
} & Pick<
  RenterTransactionProps,
  | 'amount'
  | 'dueDate'
  | 'markAsPaidEligible'
  | 'paymentCategory'
  | 'renterStatus'
  | 'scheduleMonth'
  | 'totalAmount'
  | 'zgFee'
>;

export interface AddressInfoModel {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
}
export interface IClusterSettingsModel {
  enableLatePayments: boolean;
  plaidEnv: string;
  splunkUrl: string;
  stripePublicKey: string;
}

export interface IFeaturePermissionModel {
  featurePermissions: IFeaturePermissionObject[];
}

export interface IFeaturePermissionObject {
  featureName: string;
  isAllowed: boolean;
  exhaused: boolean;
  isEnabled: boolean;
}

export interface INewPaymentMethods {
  moveInCostsId?: string;
  monthlyRentId?: string;
}
